import { Button } from "@/components/ui/button.tsx";
import { Loader } from "@/components/ui/loader.tsx";
import { Popover, PopoverContent } from "@/components/ui/popover.tsx";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { PersonAvatar } from "@packages/core-client/people/PersonAvatar.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReviewCyclesClient } from "../../../api/reviewCyclesQuery.ts";
import { reviewCyclesI18NextInstanceKey } from "../../../i18n/i18next.ts";

export interface CycleNudgeRecipientsPopoverProps {
    cycleId: string;
    memberIds: string[];
}

const RECIPIENT_PREVIEW_LIMIT = 50;

export function CycleNudgeRecipientsPopover({ cycleId, memberIds }: CycleNudgeRecipientsPopoverProps) {
    const i18nextInstance = useI18nextInstance(reviewCyclesI18NextInstanceKey);
    const { t } = useTranslation("CycleNudgeDialog", { i18n: i18nextInstance });
    const { queryUtils } = useReviewCyclesClient();
    const session = useSession();
    const [isOpen, setIsOpen] = useState(false);

    const previewIds = useMemo(() => memberIds.slice(0, RECIPIENT_PREVIEW_LIMIT), [memberIds]);
    const hiddenCount = memberIds.length - previewIds.length;

    const recipientOptions = queryUtils.listCycleReminderRecipients.queryOptions({ input: { orgId: session?.organizationId ?? "", cycleId, memberIds: previewIds } });

    const { data, isPending, isError } = useQuery(createQueryOptions(recipientOptions, { enabled: isOpen, throwOnError: false }));

    const handleOpenChange = useCallback((open: boolean) => setIsOpen(open), []);

    return (
        <Popover isOpen={isOpen} onOpenChange={handleOpenChange}>
            <Button intent="plain" size="sm" className="text-muted-fg hover:text-fg h-auto px-1 py-0 underline">
                {t("recipients.count", { count: memberIds.length })}
            </Button>
            <PopoverContent className="w-72">
                <div className="max-h-64 overflow-y-auto p-1">
                    {isPending && (
                        <div className="flex items-center gap-2 p-2">
                            <div aria-hidden="true">
                                <Loader />
                            </div>
                            <span className="text-muted-fg text-sm">{t("recipients.loading")}</span>
                        </div>
                    )}
                    {isError && <p className="text-muted-fg p-2 text-sm">{t("recipients.error")}</p>}
                    {data && (
                        <ul className="flex flex-col gap-1">
                            {data.items.map(person => (
                                <li key={person.id} className="flex min-w-0 items-center gap-2 p-1">
                                    <PersonAvatar person={person} size="xs" />
                                    <span className="text-fg min-w-0 truncate text-sm">{person.displayName}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {data && hiddenCount > 0 && <p className="text-muted-fg p-2 text-sm">{t("recipients.andMore", { count: hiddenCount })}</p>}
                </div>
            </PopoverContent>
        </Popover>
    );
}
